import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CalendarDays, Users } from "lucide-react";
import { useLang } from "@/i18n";
import { useBooking, formatRange } from "@/components/booking/useBooking";
import RangeCalendar from "@/components/booking/RangeCalendar";
import GuestsPanel from "@/components/booking/GuestsPanel";

export default function StickyBookingBar() {
    const [visible, setVisible] = useState(false);
    const { lang, t } = useLang();
    const bk = useBooking();

    useEffect(() => {
        const onScroll = () => setVisible(window.scrollY > window.innerHeight * 0.85);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const cell = (icon, label, control, testId, panel) => (
        <div
            key={testId}
            data-testid={testId}
            role="button"
            tabIndex={0}
            onClick={() => bk.openPanel(panel)}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && bk.openPanel(panel)}
            aria-haspopup="dialog"
            aria-expanded={bk.panel === panel}
            className="flex w-full min-w-0 cursor-pointer items-center gap-2 rounded-xl bg-sand/70 px-3 py-1.5 md:w-auto md:bg-transparent md:py-1"
        >
            {icon}
            <span className="flex min-w-0 flex-col">
                <span className="text-[9px] font-semibold uppercase tracking-[0.16em] text-ink-muted">{label}</span>
                {control}
            </span>
        </div>
    );

    const hasRange = Boolean(bk.arrival && bk.departure);

    return (
        <AnimatePresence>
            {visible && (
                <motion.div
                    initial={{ y: -90, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -90, opacity: 0 }}
                    transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                    className="fixed inset-x-0 top-[88px] z-40 flex justify-center px-4"
                    data-testid="sticky-booking-bar"
                >
                    <form
                        action="https://rbe.zaviaerp.com/hotel/puntapalmeras"
                        method="get"
                        onSubmit={bk.validateSubmit}
                        aria-label={t.hero.barAria}
                        className="relative grid w-full max-w-3xl grid-cols-2 items-stretch gap-1.5 rounded-2xl bg-cream/95 p-2.5 shadow-[0_16px_40px_rgba(45,51,25,0.22)] backdrop-blur md:flex md:w-auto md:items-center md:justify-center md:gap-0 md:divide-x md:divide-sand-border md:rounded-full md:px-4"
                    >
                        <input type="hidden" name="zbe_hotel_id" value="puntapalmeras" />
                        <input type="hidden" name="lng" value={lang} />
                        <input type="hidden" name="currency" value="MXN" />
                        <input type="hidden" name="zbe_arrival" value={bk.arrival} data-testid="sticky-checkin-input" />
                        <input type="hidden" name="zbe_departure" value={bk.departure} data-testid="sticky-checkout-input" />
                        <input type="hidden" name="adults" value={bk.adults} data-testid="sticky-adults-select" />
                        <input type="hidden" name="children" value={bk.children} data-testid="sticky-children-select" />
                        {cell(
                            <CalendarDays className="h-4 w-4 shrink-0 text-olive" strokeWidth={1.5} />,
                            t.hero.dates,
                            <span
                                className={`block w-full min-w-0 truncate text-xs font-medium ${hasRange ? "text-ink" : "text-ink-muted/70"}`}
                                data-testid="sticky-dates-display"
                            >
                                {formatRange(bk.arrival, bk.departure, lang) || t.hero.addDates}
                            </span>,
                            "sticky-field-dates",
                            "dates",
                        )}
                        {cell(
                            <Users className="h-4 w-4 shrink-0 text-olive" strokeWidth={1.5} />,
                            t.hero.guests,
                            <span className="block w-full min-w-0 truncate text-xs font-medium text-ink" data-testid="sticky-guests-display">
                                {t.hero.guestSummary(bk.adults, bk.children)}
                            </span>,
                            "sticky-field-guests",
                            "guests",
                        )}
                        <button
                            type="submit"
                            data-testid="sticky-booking-submit-button"
                            className="col-span-2 w-full shrink-0 whitespace-nowrap rounded-full bg-amber px-6 py-2.5 text-xs font-semibold text-white transition-all duration-300 hover:bg-amber-dark md:col-span-1 md:ml-3 md:w-auto"
                        >
                            {t.hero.consult}
                        </button>
                        {bk.panel === "dates" && (
                            <RangeCalendar start={bk.arrival} end={bk.departure} onChange={bk.setRange} onClose={bk.closePanel} />
                        )}
                        {bk.panel === "guests" && (
                            <GuestsPanel adults={bk.adults} children={bk.children} onChange={bk.setGuests} onClose={bk.closePanel} className="md:left-auto md:right-16" />
                        )}
                    </form>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
